import { MouseEventHandler } from 'react';
import { Link, LinkProps, useNavigate } from 'react-router-dom';
import { useConfirm } from 'components/modals/message/MessageProvider';

/**
 * Parameters of the LinkWithConfirm component.
 */
export interface LinkWithConfirmProps extends Omit<LinkProps, 'to'> {
  confirmMessage: string;
  confirmTitle?: string;
  bypassConfirm?: boolean;
  to: string | number;
}

/**
 * Link component which shows a confirmation dialog before navigating to the
 * given URL.
 */
export default function LinkWithConfirm({
  confirmMessage,
  to,
  confirmTitle = '',
  bypassConfirm = false,
  ...props
}: LinkWithConfirmProps) {
  const { confirm } = useConfirm();
  const navigate = useNavigate();

  /**
   * Handle the user's click on the link.
   */
  const click: MouseEventHandler<HTMLAnchorElement> = (event) => {
    // Let browser handle opening in a new tab/window (middle click, ctrl/cmd click, etc.).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return true;
    }

    event.preventDefault();
    event.stopPropagation();

    if (bypassConfirm) {
      navigate(to as any);
    } else {
      confirm({
        title: confirmTitle,
        content: confirmMessage,
        onConfirm: () => navigate(to as any),
      });
    }

    return false;
  };

  return <Link {...props} to={to as any} onClick={click} />;
}
